import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Fade,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
  OutlinedInput,
  FormControl,
  InputLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ButtonGroup,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  TableSortLabel
} from "@mui/material";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
// Force le chargement local de TinyMCE v6
import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/models/dom';
import 'tinymce-i18n/langs/fr_FR';

// Plugins COMMUNITY uniquement (rien de premium)
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/autoresize';
import 'tinymce/plugins/wordcount';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/code';
import 'tinymce/plugins/image';
import 'tinymce/plugins/searchreplace';

// CSS de skin/ui en local pour afficher l'UI sans chargement externe
import 'tinymce/skins/ui/oxide/skin.min.css';
import 'tinymce/skins/ui/oxide/content.min.css';

import { Editor } from '@tinymce/tinymce-react';
import Info from './Info';
import LanguageSelector from './LanguageSelector';
import { useAdminLanguage } from './AdminI18n';
import useTableSort from './useTableSort';

// Configuration TinyMCE pour mode self-hosted gratuit
const buildTinymceConfig = (token) => ({
  menubar: false,
  language: 'fr_FR',
  plugins: [
    'lists', 'link', 'autolink', 'autoresize', 'wordcount', 'fullscreen', 'table', 'searchreplace', 'image'
  ],
  toolbar: 'undo redo | bold italic underline strikethrough | forecolor backcolor | bullist numlist blockquote | alignleft aligncenter alignright alignjustify | indent outdent | table | image | removeformat searchreplace | fullscreen',
  content_style: 'body { font-family: Helvetica, Arial, sans-serif; font-size: 15px; }',
  branding: false,
  elementpath: false,
  statusbar: false,
  paste_as_text: false,
  paste_webkit_styles: 'none',
  paste_remove_styles: false,
  paste_merge_formats: true,
  smart_paste: false,
  // Configuration pour mode self-hosted (pas de cloud)
  promotion: false,
  contextmenu: false,
  // Évite tout chargement externe
  skin: false,
  content_css: false,
  // Hauteur minimale souhaitée (~5 lignes)
  min_height: 200,
  // Auto-resize
  autoresize_min_height: 200,
  autoresize_max_height: 600,
  autoresize_bottom_margin: 16,
  images_upload_credentials: true,
  file_picker_types: 'image',
  image_description: false,
  images_upload_handler: async (blobInfo) => {
    const formData = new FormData();
    formData.append('image', blobInfo.blob(), blobInfo.filename());

    try {
      const response = await axios.post('/api/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        withCredentials: true
      });

      const url = response.data?.url;
      if (!url) {
        throw new Error("Réponse inattendue du serveur d'upload.");
      }

      return url;
    } catch (error) {
      const message = error.response?.data?.message || "Échec de l'upload de l'image.";
      throw new Error(message);
    }
  },
  paste_postprocess: (plugin, args) => {
    // Normalisation des espaces comme dans Quill
    args.node.innerHTML = args.node.innerHTML
      .replace(/<strong>([^<]+)<\/strong>\s+<strong>([^<]+)<\/strong>/g, '<strong>$1&nbsp;$2</strong>')
      .replace(/<strong><u>([^<]+)<\/u><\/strong>\s+<strong>([^<]+)<\/strong>/g, '<strong><u>$1</u>&nbsp;$2</strong>')
      .replace(/<u><strong>([^<]+)<\/strong><\/u>\s+<strong>([^<]+)<\/strong>/g, '<strong><u>$1</u>&nbsp;$2</strong>');
  }
});

// Normalisation des espaces entre balises inline pour éviter que Quill ne les colle
const normalizeEditorHtmlSpaces = (html) => {
  if (!html || typeof html !== 'string') return html || '';
  let normalized = html;
  // Fusionner deux blocs strong avec un espace insécable unique
  normalized = normalized.replace(
    /<strong>([^<]+)<\/strong>\s+<strong>([^<]+)<\/strong>/g,
    '<strong>$1&nbsp;$2</strong>'
  );
  // Cas: <strong><u>NOM</u></strong> <strong>Prénom</strong> => <strong><u>NOM</u>&nbsp;Prénom</strong>
  normalized = normalized.replace(
    /<strong><u>([^<]+)<\/u><\/strong>\s+<strong>([^<]+)<\/strong>/g,
    '<strong><u>$1</u>&nbsp;$2</strong>'
  );
  // Cas: <u><strong>NOM</strong></u> <strong>Prénom</strong> => <strong><u>NOM</u>&nbsp;Prénom</strong>
  normalized = normalized.replace(
    /<u><strong>([^<]+)<\/strong><\/u>\s+<strong>([^<]+)<\/strong>/g,
    '<strong><u>$1</u>&nbsp;$2</strong>'
  );
  return normalized;
};

// Composant personnalisé pour l'éditeur TinyMCE
function RichTextEditor({ value, onChange, placeholder, isDark }) {
  const handleEditorChange = (content, editor) => {
    // Appliquer la normalisation des espaces
    const normalizedContent = normalizeEditorHtmlSpaces(content);
    onChange(normalizedContent);
  };

  const editorConfig = {
    ...buildTinymceConfig(sessionStorage.getItem('authToken')),
    placeholder: placeholder || "Écrivez ici le contenu de la rubrique...",
    content_style: `
      body {
        font-family: Helvetica, Arial, sans-serif;
        font-size: 15px;
        color: ${isDark ? '#ecf0f1' : 'inherit'};
        background-color: ${isDark ? '#22303e' : '#ffffff'};
      }
      .mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before {
        color: ${isDark ? '#95a5a6' : '#aaa'};
      }
    `
  };

  return (
    <Box sx={{
      '& .tox-tinymce': {
        borderRadius: '4px',
        borderColor: isDark ? '#22303e' : '#e0e0e0',
        minHeight: '120px'
      },
      '& .tox-toolbar': {
        backgroundColor: isDark ? '#21303c' : '#f5f5f5',
        borderColor: isDark ? '#22303e' : '#e0e0e0',
      },
      '& .tox-editor-container': {
        backgroundColor: isDark ? '#22303e' : '#ffffff',
        borderColor: isDark ? '#22303e' : '#e0e0e0',
      },
      mt: 1,
      mb: 5
    }}>
      <Editor
        value={normalizeEditorHtmlSpaces(value) || ''}
        init={editorConfig}
        onEditorChange={handleEditorChange}
      />
    </Box>
  );
}

const DEFAULT_CATEGORY_OPTIONS = [
  "Rubriques de base",
  "Rubriques judiciaires",
  "Rubriques accident et roulage",
  "Premières mesures",
  "Mesures secondaires",
  "Banques de données",
  "Constatations",
  "Privation de liberté",
  "Audition",
  "Avis et contacts",
  "Requisitions",
  "Enquête et recherches",
  "Rubriques de clôture"
];

const RubricAdmin = () => {
  const { t } = useAdminLanguage();
  const [rubrics, setRubrics] = useState([]);
  const [filteredRubrics, setFilteredRubrics] = useState([]);
  const [options, setOptions] = useState(DEFAULT_CATEGORY_OPTIONS);
  const [selected, setSelected] = useState([]);
  const [customOption, setCustomOption] = useState("");
  const [formData, setFormData] = useState({ 
    version: '', 
    title: '', 
    after: '', 
    obsolete: false, 
    canvasOnly: false,
    content: '',
    contentTitle: '',
    type: 'intelligent',
    keywords: []
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [keywordInput, setKeywordInput] = useState("");
  const [touchedFields, setTouchedFields] = useState({ 
    version: false, 
    title: false, 
    content: false, 
    contentTitle: false 
  });
  const [language, setLanguage] = useState('fr');
  const [scriptFile, setScriptFile] = useState(null);

  useEffect(() => {
    fetchRubrics();
  }, [language]);

  const fetchRubrics = async () => {
    try {
      const response = await fetch(`/api/rubrics?lang=${language}`);
      const data = await response.json();
      setRubrics(data);
      setFilteredRubrics(data);
      setLoading(false);
    } catch (err) {
      setError(t('Erreur lors du chargement des rubriques.'));
      setLoading(false);
    }
  };

  const handleChange = (event) => {
    setSelected(event.target.value);
  };

  const handleAddCustomOption = (event) => {
    if (event.key === "Enter" && customOption.trim() !== "") {
      if (!options.includes(customOption)) {
        setOptions([...options, customOption]);
      }
      setSelected([...selected, customOption]);
      setCustomOption("");
      event.preventDefault();
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
    setTouchedFields(prev => ({ ...prev, [id]: true }));
  };

  const handleTypeChange = (e) => {
    setFormData(prev => ({ ...prev, type: e.target.value }));
  };

  const handleCanvasOnlyChange = (e) => {
    const checked = e.target.checked;
    setFormData(prev => ({
      ...prev,
      canvasOnly: checked,
      // Une rubrique réservée aux canevas doit rester masquée de la liste générale
      ...(checked ? { obsolete: true } : {})
    }));
  };

  const handleContentChange = (value) => {
    setFormData(prev => ({ ...prev, content: value }));
    setTouchedFields(prev => ({ ...prev, content: true }));
  };

  const handleAfterChange = (e) => {
    setFormData(prev => ({ ...prev, after: e.target.value }));
  };

  const resetForm = () => {
    setFormData({ 
      version: '', 
      title: '', 
      after: '', 
      obsolete: false, 
      canvasOnly: false,
      content: '',
      contentTitle: '',
      type: 'intelligent',
      keywords: []
    });
    setSelected([]);
    setIsEditing(false);
    setEditingId(null);
    setScriptFile(null);
    setKeywordInput("");
    setTouchedFields({ 
      version: false, 
      title: false, 
      content: false, 
      contentTitle: false 
    });
  };

  const handleSubmit = async () => {
    try {
      // Validation des champs obligatoires
      if (!formData.version.trim() || !formData.title.trim()) {
        setError(t('L\'identifiant technique et le titre sont obligatoires.'));
        return;
      }

      if (formData.type === 'manual') {
        if (!formData.contentTitle.trim() || !formData.content.trim()) {
          setError(t('Pour une rubrique manuelle, le titre et le contenu sont obligatoires.'));
          return;
        }
      }

      // Si création d'une rubrique générative, exiger le fichier .js
      if (!isEditing && formData.type === 'intelligent') {
        if (!scriptFile) {
          setError(t('Veuillez sélectionner le fichier .js de la rubrique.'));
          return;
        }
        if (!scriptFile.name.toLowerCase().endsWith('.js')) {
          setError(t('Seuls les fichiers .js sont acceptés pour les rubriques génératives.'));
          return;
        }
      }

      setLoading(true);
      setError("");
      setSuccess("");
      
      const newRubric = {
        version: formData.version,
        title: formData.title,
        obsolete: formData.obsolete,
        canvasOnly: formData.canvasOnly,
        type: formData.type,
        categories: selected,
        keywords: formData.keywords,
        ...(formData.type === 'manual' && {
          'content-title': formData.contentTitle,
          content: formData.content
        })
      };

      if (isEditing) {
        // N'envoyer after que si l'utilisateur a choisi une position
        const updatePayload = { ...newRubric, lang: language };
        if (formData.after) {
          updatePayload.after = formData.after;
        }
        await fetch(`/api/rubrics/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatePayload),
        });
        setSuccess(t('Rubrique mise à jour avec succès.'));
      } else {
        if (formData.type === 'intelligent') {
          // Envoi multipart pour inclure le fichier .js
          const fd = new FormData();
          fd.append('version', newRubric.version);
          fd.append('title', newRubric.title);
          fd.append('after', formData.after || '');
          fd.append('obsolete', String(!!newRubric.obsolete));
          fd.append('canvasOnly', String(!!newRubric.canvasOnly));
          fd.append('type', newRubric.type);
          fd.append('categories', JSON.stringify(newRubric.categories || []));
          fd.append('keywords', JSON.stringify(newRubric.keywords || []));
          fd.append('lang', language);
          fd.append('script', scriptFile, `${newRubric.version}.js`);

          const response = await fetch('/api/rubrics', {
            method: 'POST',
            body: fd,
          });
          if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(data?.error || t('Erreur lors de l\'enregistrement de la rubrique.'));
          }
        } else {
          await fetch('/api/rubrics', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...newRubric, after: formData.after || '', lang: language }),
          });
        }
        setSuccess(t('Rubrique ajoutée avec succès.'));
      }

      resetForm();
      fetchRubrics();
    } catch (err) {
      setError(err?.message || t('Erreur lors de l\'enregistrement de la rubrique.'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (rubric) => {
    setIsEditing(true);
    setEditingId(rubric.id);
    setFormData({ 
      version: rubric.version, 
      title: rubric.title, 
      after: '', 
      obsolete: rubric.obsolete, 
      canvasOnly: !!rubric.canvasOnly,
      content: rubric.content || '',
      contentTitle: rubric['content-title'] || '',
      type: rubric.type || 'intelligent',
      keywords: rubric.keywords || []
    });
    setSelected(rubric.categories || []);
    setKeywordInput("");
    setScriptFile(null);
  };

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !formData.keywords.includes(keywordInput.trim())) {
      setFormData(prev => ({
        ...prev,
        keywords: [...prev.keywords, keywordInput.trim()]
      }));
      setKeywordInput("");
    }
  };

  const handleKeywordKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  const handleRemoveKeyword = (keywordToRemove) => {
    setFormData(prev => ({
      ...prev,
      keywords: prev.keywords.filter(keyword => keyword !== keywordToRemove)
    }));
  };

  const moveRubric = async (id, direction) => {
    try {
      setLoading(true);
      await fetch(`/api/rubrics/move/${id}?lang=${language}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ direction }),
      });
      fetchRubrics();
    } catch (err) {
      setError(t('Erreur lors du déplacement de la rubrique.'));
    } finally {
      setLoading(false);
    }
  };

  const toggleObsolete = async (id) => {
    try {
      setLoading(true);
      await fetch(`/api/rubrics/toggle-obsolete/${id}?lang=${language}`, {
        method: 'PUT',
      });
      fetchRubrics();
    } catch (err) {
      setError(t('Erreur lors de la modification du statut de la rubrique.'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('Êtes-vous sûr de vouloir supprimer cette rubrique ?'))) {
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");
      
      const response = await fetch(`/api/rubrics/${id}?lang=${language}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(t('Erreur lors de la suppression'));
      }

      setSuccess(t('Rubrique supprimée avec succès.'));
      fetchRubrics();
    } catch (err) {
      setError(t('Erreur lors de la suppression de la rubrique.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    const filtered = rubrics.filter(
      (rubric) =>
        rubric.version.toLowerCase().includes(query) ||
        rubric.title.toLowerCase().includes(query)
    );
    setFilteredRubrics(filtered);
  };

  const columns = useMemo(() => ([
    { id: 'version', label: t('Identifiant'), sortable: true },
    { id: 'title', label: t('Titre'), sortable: true },
    { id: 'type', label: t('Type'), sortable: true },
    { id: 'categories', label: t('Catégories'), sortable: false },
    { id: 'status', label: t('Statut'), sortable: true },
  ]), [t]);

  const getComparableValue = (rubric, property) => {
    switch (property) {
      case 'version':
      case 'title':
        return rubric[property]?.toString().toLowerCase() || '';
      case 'type':
        return rubric.type || '';
      case 'status':
        return rubric.obsolete ? 1 : 0;
      default:
        return rubric[property];
    }
  };

  const { order, orderBy, sortedRows, handleRequestSort } = useTableSort(filteredRubrics, {
    defaultOrderBy: null,
    getComparableValue,
  });

  const renderCellContent = (rubric, columnId) => {
    switch (columnId) {
      case 'type':
        return (
          <Chip 
            label={rubric.type === 'intelligent' ? t('Générative') : t('Manuelle')} 
            color={rubric.type === 'intelligent' ? 'primary' : 'secondary'}
            size="small" 
          />
        );
      case 'categories':
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {rubric.categories && rubric.categories.map((category) => (
              <Chip key={category} label={t(category)} size="small" />
            ))}
          </Box>
        );
      case 'status':
        if (rubric.canvasOnly) {
          return <Chip icon={<InfoIcon />} label={t('Canevas')} color="info" size="small" />;
        }
        return rubric.obsolete ? (
          <Chip icon={<BlockIcon />} label={t('Masquée')} color="error" size="small" />
        ) : (
          <Chip icon={<CheckCircleIcon />} label={t('Visible')} color="success" size="small" />
        );
      default:
        return rubric[columnId];
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in timeout={500}>
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            color="primary"
            sx={{ fontWeight: 'bold', mb: 4 }}
          >
            {t('Gestion des rubriques')}
          </Typography>

          <Info type="rubric" />

          <LanguageSelector 
            language={language} 
            onLanguageChange={setLanguage} 
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              {t(isEditing ? 'Modifier la rubrique' : 'Ajouter une rubrique')}
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="version"
                  label={t('Identifiant technique')}
                  value={formData.version}
                  onChange={handleInputChange}
                  variant="outlined"
                  disabled={loading}
                  required
                  autoComplete="off"
                  error={touchedFields.version && !formData.version.trim()}
                  helperText={touchedFields.version && !formData.version.trim() ? t('Ce champ est obligatoire') : ''}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="title"
                  label={t("Titre dans la liste d'ajout")}
                  value={formData.title}
                  onChange={handleInputChange}
                  variant="outlined"
                  disabled={loading}
                  required
                  autoComplete="off"
                  error={touchedFields.title && !formData.title.trim()}
                  helperText={touchedFields.title && !formData.title.trim() ? t('Ce champ est obligatoire') : ''}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!formData.canvasOnly}
                      onChange={handleCanvasOnlyChange}
                      disabled={loading}
                    />
                  }
                  label={t("Cette rubrique fait exclusivement partie d'un ou de plusieurs canevas et ne doit être accessible que par ces canevas (Ne sera pas visible dans la liste générale)")}
                />
              </Grid>

              {!formData.canvasOnly && (
                <>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>{t('Catégories')}</InputLabel>
                      <Select
                        multiple
                        value={selected}
                        onChange={handleChange}
                        input={<OutlinedInput label={t('Catégories')} />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip key={value} label={t(value)} />
                            ))}
                          </Box>
                        )}
                        disabled={loading}
                      >
                        {options.map((option) => (
                          <MenuItem key={option} value={option}>
                            <Checkbox checked={selected.includes(option)} />
                            <ListItemText primary={t(option)} />
                          </MenuItem>
                        ))}
                        <MenuItem>
                          <TextField
                            fullWidth
                            placeholder={t('Ajouter une option') || 'Ajouter une option'}
                            value={customOption}
                            onChange={(e) => setCustomOption(e.target.value)}
                            onKeyDown={handleAddCustomOption}
                            variant="standard"
                            autoComplete="off"
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                          />
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>{t('Placer après')}</InputLabel>
                      <Select
                        id="after"
                        value={formData.after || ""}
                        onChange={handleAfterChange}
                        label={t('Placer après')}
                        disabled={loading}
                      >
                        <MenuItem value="">{t('Début de la liste')}</MenuItem>
                        {rubrics.map((rubric) => (
                          <MenuItem key={rubric.id} value={rubric.id}>{rubric.title}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                  <FormLabel component="legend">{t('Mots-clés pour la recherche')}</FormLabel>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <TextField
                        fullWidth
                        value={keywordInput}
                        onChange={(e) => setKeywordInput(e.target.value)}
                        onKeyPress={handleKeywordKeyPress}
                        placeholder={t('Ajouter un mot-clé')}
                        variant="outlined"
                        size="small"
                        disabled={loading}
                        autoComplete="off"
                      />
                      <Button
                        onClick={handleAddKeyword}
                        variant="outlined"
                        startIcon={<AddIcon />}
                        disabled={loading || !keywordInput.trim()}
                        sx={{ minWidth: 'auto', px: 2 }}
                      >
                        {t('Ajouter')}
                      </Button>
                    </Box>
                    {formData.keywords.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {formData.keywords.map((keyword, index) => (
                          <Chip
                            key={index}
                            label={keyword}
                            onDelete={() => handleRemoveKeyword(keyword)}
                            deleteIcon={<CloseIcon />}
                            color="primary"
                            variant="outlined"
                            size="small"
                          />
                        ))}
                      </Box>
                    )}
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">{t('Type de rubrique') || t('Type')}</FormLabel>
                  <RadioGroup
                    value={formData.type}
                    onChange={handleTypeChange}
                    row
                  >
                    <FormControlLabel 
                      value="intelligent" 
                      control={<Radio />} 
                      label={t('Générative')} 
                    />
                    <FormControlLabel 
                      value="manual" 
                      control={<Radio />} 
                      label={t('Manuelle')} 
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {formData.type === 'intelligent' && !isEditing && (
                <Grid item xs={12}>
                  <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
                    {t('Sélectionnez le fichier .js de la rubrique. Il sera enregistré automatiquement dans le bon dossier selon la langue, avec le nom de l\'identifiant technique.')} 
                  </Alert>
                  <Button variant="outlined" component="label" disabled={loading}>
                    {t(scriptFile ? 'Fichier sélectionné' : 'Choisir un fichier .js')}
                    <input
                      type="file"
                      accept=".js,application/javascript,text/javascript"
                      hidden
                      onChange={(e) => setScriptFile(e.target.files?.[0] || null)}
                    />
                  </Button>
                  {scriptFile && (
                    <Typography variant="body2" sx={{ ml: 2, display: 'inline-block' }}>
                      {scriptFile.name}
                    </Typography>
                  )}
                </Grid>
              )}

              {formData.type === 'manual' && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id="contentTitle"
                      label={t('Titre de la rubrique après ajout')}
                      value={formData.contentTitle}
                      onChange={handleInputChange}
                      variant="outlined"
                      disabled={loading}
                      required
                      autoComplete="off"
                      error={touchedFields.contentTitle && !formData.contentTitle.trim()}
                      helperText={touchedFields.contentTitle && !formData.contentTitle.trim() ? t('Ce champ est obligatoire pour une rubrique manuelle') : ''}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <RichTextEditor
                        value={formData.content}
                        onChange={handleContentChange}
                        placeholder={t('Écrivez ici le contenu de la rubrique...') || 'Écrivez ici le contenu de la rubrique...'}
                      />
                      {touchedFields.content && !formData.content.trim() && (
                        <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                          {t('Ce champ est obligatoire pour une rubrique manuelle')}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={loading}
                    startIcon={loading && <CircularProgress size={20} />}
                    sx={{
                      py: 1.5,
                      px: 4,
                      fontSize: '1rem',
                      textTransform: 'none',
                      transition: 'all 0.3s',
                      '&:hover': { boxShadow: 6 },
                    }}
                  >
                    {loading ? t('Enregistrement...') : t(isEditing ? 'Enregistrer' : 'Ajouter')}
                  </Button>
                  {isEditing && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={resetForm}
                      disabled={loading}
                      sx={{
                        py: 1.5,
                        px: 4,
                        fontSize: '1rem',
                        textTransform: 'none',
                      }}
                    >
                      {t('Annuler')}
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              {t('Liste des rubriques')}
            </Typography>
            <TextField
              fullWidth
              label={t('Rechercher une rubrique')}
              placeholder={t('Version ou titre de rubrique')}
              value={searchQuery}
              onChange={handleSearch}
              variant="outlined"
              autoComplete="off"
              sx={{ mb: 2 }}
            />

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        sortDirection={orderBy === column.id ? order : false}
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            active={orderBy === column.id}
                            direction={orderBy === column.id ? order : 'asc'}
                            onClick={() => handleRequestSort(column.id)}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                    <TableCell>{t('Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRows.map((rubric, index) => (
                    <TableRow key={rubric.id}>
                      {columns.map((column) => (
                        <TableCell key={`${rubric.id}-${column.id}`}>
                          {renderCellContent(rubric, column.id)}
                        </TableCell>
                      ))}
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'nowrap' }}>
                          <IconButton
                            onClick={() => moveRubric(rubric.id, 'up')}
                            disabled={index === 0 || loading || !!orderBy}
                            color="primary"
                            size="small"
                            title={orderBy ? t('Désactivez le tri pour réordonner') : t('Déplacer vers le haut')}
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => moveRubric(rubric.id, 'down')}
                            disabled={index === sortedRows.length - 1 || loading || !!orderBy}
                            color="primary"
                            size="small"
                            title={orderBy ? t('Désactivez le tri pour réordonner') : t('Déplacer vers le bas')}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => handleEdit(rubric)}
                            color="primary"
                            size="small"
                            disabled={loading}
                            title={t('Modifier')}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDelete(rubric.id)}
                            color="error"
                            size="small"
                            disabled={loading}
                            title={t('Supprimer')}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          <Button
                            onClick={() => toggleObsolete(rubric.id)}
                            color={rubric.obsolete ? "success" : "error"}
                            size="small"
                            disabled={loading || (rubric.canvasOnly && rubric.obsolete)}
                            startIcon={rubric.obsolete ? <CheckCircleIcon fontSize="small" /> : <BlockIcon fontSize="small" />}
                            sx={{ minWidth: 'auto', px: 1, fontSize: '0.75rem' }}
                          >
                            {rubric.obsolete ? t('Afficher') : t('Masquer')}
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Fade>
    </Container>
  );
};

export default RubricAdmin;